import type { OpenLoan, OpenLoanCheckApplicantResponse } from "@credit-acceptance/step6-bff-client";
import { Skeleton, toast } from "@credit-acceptance/uikit";
import { CircleAlertIcon, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { DateInput } from "../../components/ui/uikit/dateInput";
import { closeCalendarOverlay, isCalendarOverlayOpen } from "../../lib/calendarOverlay";
import { getErrorMessage } from "../../lib/query/errors";
import { cn } from "../../lib/utils";
import { useFetchLetterContent } from "./api/useFetchLetterContent";
import { buildPayoffLetterRequest, useGeneratePayoffLetter } from "./api/useGeneratePayoffLetter";
import { buildSettlementLetterRequest, useGenerateSettlementLetter } from "./api/useGenerateSettlementLetter";
import { useOpenLoanCheck } from "./api/useOpenLoanCheck";

interface OpenLoanCheckModalProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  approvalId?: string;
  readOnly?: boolean;
}

const usdFormatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

function formatAmount(amount: number | undefined): string {
  return amount === undefined || !Number.isFinite(amount) ? "" : usdFormatter.format(amount);
}

function loanName(loan: OpenLoan): string {
  return [loan.firstName, loan.middleName, loan.lastName].filter(Boolean).join(" ");
}

function loanVehicle(loan: OpenLoan): string {
  const { year, make, model } = loan.vehicleInfo ?? {};
  return [year, make, model].filter(Boolean).join(" ");
}

const POSITIVE_INTEGER = /^\d+$/;

function isValidMileage(value: string): boolean {
  const trimmed = value.trim();
  return POSITIVE_INTEGER.test(trimmed) && Number(trimmed) > 0;
}

type LastLetterAction = "payoff" | "settlement" | undefined;

function LoanCard({ loan, readOnly }: { loan: OpenLoan; readOnly: boolean }) {
  const { t } = useTranslation();
  const vehicle = loanVehicle(loan);

  const [payoffDate, setPayoffDate] = useState("");
  const [mileage, setMileage] = useState("");
  const [lastAction, setLastAction] = useState<LastLetterAction>();

  const payoffMutation = useGeneratePayoffLetter();
  const settlementMutation = useGenerateSettlementLetter();
  const contentMutation = useFetchLetterContent();

  const mileageRequired = !!loan.isVscMileageFlag;
  const mileageValid = !mileageRequired || isValidMileage(mileage);
  const mileageError = mileage.trim() !== "" && !isValidMileage(mileage);
  const noLettersAvailable = !loan.isPayOffLetterAllowed && !loan.isSettlementLetterAllowed;
  // Payoff and settlement share one contentMutation/lastAction (the banner
  // only ever reflects the most recent action), so a second action started
  // while the first is still in flight would race and overwrite the first's
  // status. Disabling both buttons whenever any of the three mutations is
  // pending keeps only one action in flight at a time.
  const anyLetterActionPending = payoffMutation.isPending || settlementMutation.isPending || contentMutation.isPending;
  const lettersDisabled = readOnly || (mileageRequired && !mileageValid) || anyLetterActionPending;

  function letterInput() {
    return {
      payoffDate,
      quoteMileage: isValidMileage(mileage) ? Number(mileage.trim()) : undefined,
    };
  }

  function openGeneratedLetter(kind: "payoff" | "settlement", documentId: string | undefined, popup: Window | null) {
    if (!documentId) {
      popup?.close();
      return;
    }
    contentMutation.mutate({ kind, appId: loan.creditApplicationId ?? "", documentId, popup });
  }

  function handleGetPayoffLetter() {
    if (readOnly || !loan.isPayOffLetterAllowed) {
      toast.error(t("workbench.actionNotAllowed"));
      return;
    }
    const popup = window.open("about:blank", "_blank");
    setLastAction("payoff");
    payoffMutation.mutate(buildPayoffLetterRequest(loan, letterInput()), {
      onSuccess: (response) => openGeneratedLetter("payoff", response.documentId, popup),
      onError: () => popup?.close(),
    });
  }

  function handleGetSettlementLetter() {
    if (readOnly || !loan.isSettlementLetterAllowed) {
      toast.error(t("workbench.actionNotAllowed"));
      return;
    }
    const popup = window.open("about:blank", "_blank");
    setLastAction("settlement");
    settlementMutation.mutate(buildSettlementLetterRequest(loan, letterInput()), {
      onSuccess: (response) => openGeneratedLetter("settlement", response.documentId, popup),
      onError: () => popup?.close(),
    });
  }

  const activeMutation =
    lastAction === "payoff" ? payoffMutation : lastAction === "settlement" ? settlementMutation : undefined;
  const banner = contentMutation.isError
    ? {
        className: "bg-destructive/10 text-destructive",
        title: t("stipulations.openLoanCheckModal.openLetterError", {
          message: getErrorMessage(contentMutation.error),
        }),
      }
    : activeMutation?.isError
      ? {
          className: "bg-destructive/10 text-destructive",
          title: t("stipulations.openLoanCheckModal.generateError", {
            message: getErrorMessage(activeMutation.error),
          }),
        }
      : contentMutation.isPending
        ? {
            className: "bg-muted text-muted-foreground",
            title: t("stipulations.openLoanCheckModal.openingLetter"),
          }
        : activeMutation?.isSuccess
          ? activeMutation.data.documentId
            ? {
                className: "bg-success text-success-foreground",
                title: t("stipulations.openLoanCheckModal.generateSuccess", {
                  documentId: activeMutation.data.documentId,
                }),
              }
            : {
                className: "bg-destructive/10 text-destructive",
                title: t("stipulations.openLoanCheckModal.generateMissingDocument"),
              }
          : undefined;

  function renderIdentity(className: string) {
    return (
      <div className={className}>
        <p className="font-semibold">{loanName(loan)}</p>
        {vehicle && <p>{vehicle}</p>}
        <p>
          <span className="text-muted-foreground">{t("stipulations.openLoanCheckModal.approvalNumberLabel")}: </span>
          {loan.creditApplicationId}
        </p>
      </div>
    );
  }

  const hasDescription = !!loan.disclosureMessage || (loan.isSecondLienPresent && !!loan.secondLienMessage);

  return (
    <div className="border-border grid grid-cols-1 items-start gap-x-4 gap-y-2 rounded border p-3 text-xs sm:grid-cols-4">
      {noLettersAvailable ? (
        <>
          {renderIdentity("flex flex-col gap-0.5")}
          <span className="text-muted-foreground">{t("stipulations.openLoanCheckModal.payoffUnavailableLabel")}</span>
          <span>{t("stipulations.openLoanCheckModal.notAvailableOnline")}</span>
          <Button type="button" variant="outline" size="xs" disabled className="justify-self-start">
            {t("stipulations.openLoanCheckModal.getPayoffLetter")}
          </Button>
          {!loan.disclosureMessage && (
            <p className="text-muted-foreground sm:col-span-4">
              {t("stipulations.openLoanCheckModal.notAvailableOnlineNote")}
            </p>
          )}
        </>
      ) : (
        <>
          {renderIdentity("flex flex-col gap-0.5 sm:row-span-3")}

          <span className="text-muted-foreground">{t("stipulations.openLoanCheckModal.pastDueLabel")}</span>
          <span>{formatAmount(loan.payOffQuote?.pastDueAmount)}</span>
          <span />

          <span className="text-muted-foreground">{t("stipulations.openLoanCheckModal.payoffLabel")}</span>
          <span>{formatAmount(loan.payOffQuote?.payOffAmount)}</span>
          <span />

          {loan.isPayOffAmntFlag ? (
            <>
              <label htmlFor={`payoff-date-${loan.creditApplicationId}`} className="text-muted-foreground">
                {t("stipulations.openLoanCheckModal.payoffDateLabel")}
              </label>
              <DateInput
                id={`payoff-date-${loan.creditApplicationId}`}
                value={payoffDate}
                onChange={setPayoffDate}
                disabled={readOnly}
              />
            </>
          ) : (
            <>
              <span />
              <span />
            </>
          )}
          <div className="flex flex-wrap gap-2 justify-self-start">
            {loan.isPayOffLetterAllowed && (
              <Button type="button" size="xs" onClick={handleGetPayoffLetter} disabled={lettersDisabled}>
                {payoffMutation.isPending && lastAction === "payoff"
                  ? t("stipulations.openLoanCheckModal.generating")
                  : t("stipulations.openLoanCheckModal.getPayoffLetter")}
              </Button>
            )}
            {loan.isSettlementLetterAllowed && (
              <Button type="button" size="xs" onClick={handleGetSettlementLetter} disabled={lettersDisabled}>
                {settlementMutation.isPending && lastAction === "settlement"
                  ? t("stipulations.openLoanCheckModal.generating")
                  : t("stipulations.openLoanCheckModal.getSettlementLetter")}
              </Button>
            )}
          </div>

          {mileageRequired && (
            <>
              <span />
              <label htmlFor={`mileage-${loan.creditApplicationId}`} className="text-muted-foreground">
                {t("stipulations.openLoanCheckModal.mileageLabel")}
              </label>
              <div className="flex flex-col gap-1">
                <Input
                  id={`mileage-${loan.creditApplicationId}`}
                  value={mileage}
                  onChange={(event) => setMileage(event.target.value)}
                  aria-invalid={mileageError}
                  disabled={readOnly}
                />
                {mileageError && (
                  <span className="text-destructive">{t("stipulations.openLoanCheckModal.mileageInvalid")}</span>
                )}
              </div>
              <span />
            </>
          )}

          {banner && <div className={cn("rounded-md px-3 py-2 sm:col-span-4", banner.className)}>{banner.title}</div>}
        </>
      )}

      {hasDescription && (
        <div className="flex flex-col gap-1 sm:col-span-4">
          {loan.disclosureMessage && <p className="text-muted-foreground">{loan.disclosureMessage}</p>}
          {loan.isSecondLienPresent && loan.secondLienMessage && (
            <p className="text-destructive font-semibold">{loan.secondLienMessage}</p>
          )}
        </div>
      )}
    </div>
  );
}

function applicantMessages(data: {
  driver?: OpenLoanCheckApplicantResponse;
  cosigner?: OpenLoanCheckApplicantResponse;
}) {
  return [data.driver, data.cosigner]
    .filter((applicant): applicant is OpenLoanCheckApplicantResponse => !!applicant?.isOpenLoanExist)
    .map((applicant) => applicant.actionMessage)
    .filter((message): message is string => !!message);
}

// Opened from the stip row's Open Loan Check button. The button click sets
// `open`, which drives `useOpenLoanCheck`'s `enabled` — the Dialog itself only
// becomes visible once the check resolves with qualifying data or errors. When
// it resolves empty, this never renders anything (caps2 parity: no modal, no
// empty state) and resets `open` so a later click re-fires the check. On
// error, the Dialog stays open showing the error instead of closing, since a
// closed modal would give the user no way to see what went wrong.
function OpenLoanCheckModal({ open, onOpenChange, approvalId, readOnly = false }: OpenLoanCheckModalProperties) {
  const { t } = useTranslation();
  const { data, isError, error, isLoading } = useOpenLoanCheck(approvalId ?? "", { enabled: open });

  const hasQualifyingData = !!(data?.driver?.isOpenLoanExist || data?.cosigner?.isOpenLoanExist);

  useEffect(() => {
    if (open && !isError && data && !hasQualifyingData) {
      onOpenChange(false);
    }
  }, [open, data, hasQualifyingData, isError, onOpenChange]);

  const openLoanList = data?.openLoanList ?? [];
  const messages = data ? applicantMessages(data) : [];

  return (
    // `modal={false}`: uikit's DatePicker portals its calendar into document.body
    // from its own bundled copy of Radix, which shares no layer/focus state with the
    // copy this Dialog uses. Under a modal Dialog that copy leaves the calendar with
    // the `pointer-events: none` Radix puts on <body>, so day cells cannot be clicked
    // at all. Dropping modal mode removes that: the overlay still blocks the page and
    // `onInteractOutside` still refuses outside dismissal, so nothing about how this
    // dialog behaves changes.
    <Dialog open={open && (isLoading || hasQualifyingData || isError)} onOpenChange={onOpenChange} modal={false}>
      <DialogContent
        showCloseButton={false}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => {
          // Escape belongs to the calendar while one is open. The DatePicker's Radix
          // copy shares no layer stack with this Dialog's, so both would dismiss on
          // the same keypress, closing the whole modal along with the calendar.
          if (isCalendarOverlayOpen()) {
            event.preventDefault();
            closeCalendarOverlay();
          }
        }}
        className="max-w-3xl gap-0 overflow-hidden bg-white p-0"
      >
        <div className="bg-primary text-primary-foreground flex items-center justify-between border-b px-4 py-2">
          <DialogTitle className="text-primary-foreground text-sm font-semibold">
            {t("stipulations.openLoanCheckModal.title")}
          </DialogTitle>
          <DialogDescription className="sr-only">{t("stipulations.openLoanCheckModal.description")}</DialogDescription>
          <DialogClose className="text-primary-foreground/80 hover:text-primary-foreground p-0.5 transition-colors">
            <X className="size-4.5" />
            <span className="sr-only">{t("stipulations.openLoanCheckModal.close")}</span>
          </DialogClose>
        </div>

        <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto p-6">
          {isLoading ? (
            <div role="status" aria-label={t("stipulations.openLoanCheckModal.loading")}>
              <Skeleton loading active paragraph={{ rows: 6 }} />
            </div>
          ) : isError ? (
            <p>{t("stipulations.openLoanCheckModal.checkError", { message: getErrorMessage(error) })}</p>
          ) : (
            <>
              {messages.length > 0 && (
                <div className="border-warning/40 bg-warning/10 flex items-start gap-3 rounded-md border p-3">
                  <CircleAlertIcon
                    className="text-warning-foreground size-6 shrink-0"
                    fill="currentColor"
                    stroke="var(--color-card)"
                    aria-hidden="true"
                  />
                  <div className="flex flex-col gap-1 text-sm font-semibold">
                    {messages.map((message, index) => (
                      <p key={index}>{message}</p>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3">
                {openLoanList.map((loan, index) => (
                  <LoanCard key={loan.creditApplicationId ?? index} loan={loan} readOnly={readOnly} />
                ))}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="justify-center border-t px-4 py-2 sm:justify-center">
          <DialogClose asChild>
            <Button type="button" size="xs">
              {t("stipulations.openLoanCheckModal.close")}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default OpenLoanCheckModal;
